import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  createScheduleItem,
  getScheduleItems,
  updateScheduleItem,
  deleteScheduleItem,
  timeToMinutes
} from '../firebase/dailyScheduleService';
import { getClasses } from '../firebase/lessonPlannerService';
import DashboardLayout from './DashboardLayout';
import './DailySummaryManager.css';

// Utility function to convert 24-hour time to 12-hour AM/PM format
const formatTime12Hour = (time24) => {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

function DailySummaryManager() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [scheduleItems, setScheduleItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    type: 'class', // 'class' or 'timer'
    classId: '',
    period: '',
    timerName: '',
    timerType: '', // 'seats' or 'bell'
    startTime: '08:00',
    endTime: '08:45',
    daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    bellCountdownEnabled: false,
    bellCountdownMinutes: 2,
    tardyCountdownEnabled: false,
    tardyCountdownMinutes: 1
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [scheduleResult, classesResult] = await Promise.all([
      getScheduleItems(currentUser.uid),
      getClasses(currentUser.uid)
    ]);

    if (scheduleResult.success) {
      setScheduleItems(scheduleResult.items);
    }
    if (classesResult.success) {
      setClasses(classesResult.classes);
    }
    setLoading(false);
  };

  const handleCreateItem = () => {
    setEditingItem(null);
    setItemForm({
      type: 'class',
      classId: '',
      period: '',
      timerName: '',
      timerType: '',
      startTime: '08:00',
      endTime: '08:45',
      daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      bellCountdownEnabled: false,
      bellCountdownMinutes: 2,
      tardyCountdownEnabled: false,
      tardyCountdownMinutes: 1
    });
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      type: item.type,
      classId: item.classId || '',
      period: item.period || '',
      timerName: item.timerName || '',
      timerType: item.timerType || '',
      startTime: item.startTime,
      endTime: item.endTime || '',
      daysOfWeek: item.daysOfWeek || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      bellCountdownEnabled: item.bellCountdownEnabled || false,
      bellCountdownMinutes: item.bellCountdownMinutes || 2,
      tardyCountdownEnabled: item.tardyCountdownEnabled || false,
      tardyCountdownMinutes: item.tardyCountdownMinutes || 1
    });
    setShowModal(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();

    if (itemForm.type === 'class' && !itemForm.classId) {
      alert('Please select a class');
      return;
    }

    if (itemForm.type === 'timer' && (!itemForm.timerName || !itemForm.timerType)) {
      alert('Please fill in timer details');
      return;
    }

    const itemData = {
      ...itemForm
    };

    let result;
    if (editingItem) {
      result = await updateScheduleItem(editingItem.id, itemData);
    } else {
      result = await createScheduleItem(currentUser.uid, itemData);
    }

    if (result.success) {
      await loadData();
      closeModal();
    } else {
      alert('Error saving schedule item. Please try again.');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this schedule item?')) {
      const result = await deleteScheduleItem(itemId);
      if (result.success) {
        await loadData();
      } else {
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const toggleDay = (day) => {
    setItemForm(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const getClassName = (classId) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj?.name || 'Unknown Class';
  };

  const sortedItems = [...scheduleItems].sort((a, b) => {
    return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading schedule...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="daily-summary-manager">
        <div className="manager-header">
          <div>
            <h1>Daily Schedule Manager</h1>
            <p className="subtitle">Configure your daily class schedule and timers</p>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/daily-summary')} className="view-summary-btn">
              📺 View Live Summary
            </button>
            <button onClick={handleCreateItem} className="create-btn">
              + Add Item
            </button>
          </div>
        </div>

        <div className="schedule-timeline">
          <div className="timeline-header">
            <h2>Daily Schedule (8:00 AM - 4:00 PM)</h2>
            <p>Classes and timers sorted by time</p>
          </div>

          {sortedItems.length === 0 ? (
            <div className="empty-state">
              <h3>No Schedule Items</h3>
              <p>Add classes or timers to build your daily schedule</p>
              <button onClick={handleCreateItem} className="create-btn-large">
                + Add First Item
              </button>
            </div>
          ) : (
            <div className="schedule-list">
              {sortedItems.map(item => (
                <div
                  key={item.id}
                  className={`schedule-item ${item.type}`}
                >
                  <div className="item-time">
                    <div className="time-badge">{formatTime12Hour(item.startTime)}</div>
                    {item.endTime && <div className="time-badge">{formatTime12Hour(item.endTime)}</div>}
                  </div>

                  <div className="item-content">
                    {item.type === 'class' ? (
                      <>
                        <div className="item-icon">📚</div>
                        <div className="item-details">
                          <h3>{getClassName(item.classId)}</h3>
                          <p className="item-meta">
                            Class Period • {formatTime12Hour(item.startTime)} - {formatTime12Hour(item.endTime)}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`item-icon timer-icon ${item.timerType}`}>
                          {item.timerType === 'seats' ? '🪑' : '🔔'}
                        </div>
                        <div className="item-details">
                          <h3>{item.timerName}</h3>
                          <p className="item-meta">
                            {item.timerType === 'seats' ? 'In Seats Warning' : 'Bell Warning'} • {formatTime12Hour(item.startTime)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="item-days">
                    {['M', 'T', 'W', 'Th', 'F'].map((day, idx) => {
                      const fullDay = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'][idx];
                      const active = item.daysOfWeek?.includes(fullDay);
                      return (
                        <span key={day} className={`day-badge ${active ? 'active' : ''}`}>
                          {day}
                        </span>
                      );
                    })}
                  </div>

                  <div className="item-actions">
                    <button onClick={() => handleEditItem(item)} className="edit-btn">
                      ✏️
                    </button>
                    <button onClick={() => handleDeleteItem(item.id)} className="delete-btn">
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>×</button>
              <h2>{editingItem ? 'Edit Schedule Item' : 'Add Schedule Item'}</h2>

              <form onSubmit={handleSaveItem} className="schedule-form">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={itemForm.type}
                    onChange={(e) => setItemForm({ ...itemForm, type: e.target.value })}
                  >
                    <option value="class">Class Period</option>
                    <option value="timer">Timer/Warning</option>
                  </select>
                </div>

                {itemForm.type === 'class' ? (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Class *</label>
                        <select
                          value={itemForm.classId}
                          onChange={(e) => setItemForm({ ...itemForm, classId: e.target.value })}
                          required
                        >
                          <option value="">Select a class...</option>
                          {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Period</label>
                        <input
                          type="text"
                          value={itemForm.period}
                          onChange={(e) => setItemForm({ ...itemForm, period: e.target.value })}
                          placeholder="e.g., 1, 2, 3..."
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Start Time *</label>
                        <input
                          type="time"
                          value={itemForm.startTime}
                          onChange={(e) => setItemForm({ ...itemForm, startTime: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>End Time *</label>
                        <input
                          type="time"
                          value={itemForm.endTime}
                          onChange={(e) => setItemForm({ ...itemForm, endTime: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={itemForm.tardyCountdownEnabled}
                          onChange={(e) => setItemForm({ ...itemForm, tardyCountdownEnabled: e.target.checked })}
                        />
                        <span>Tardy Countdown</span>
                      </label>
                      {itemForm.tardyCountdownEnabled && (
                        <div className="countdown-input">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={itemForm.tardyCountdownMinutes}
                            onChange={(e) => setItemForm({ ...itemForm, tardyCountdownMinutes: parseInt(e.target.value) || 1 })}
                          />
                          <span>minute(s) before class starts</span>
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={itemForm.bellCountdownEnabled}
                          onChange={(e) => setItemForm({ ...itemForm, bellCountdownEnabled: e.target.checked })}
                        />
                        <span>Bell Countdown</span>
                      </label>
                      {itemForm.bellCountdownEnabled && (
                        <div className="countdown-input">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={itemForm.bellCountdownMinutes}
                            onChange={(e) => setItemForm({ ...itemForm, bellCountdownMinutes: parseInt(e.target.value) || 1 })}
                          />
                          <span>minute(s) before class ends</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Timer Name *</label>
                      <input
                        type="text"
                        value={itemForm.timerName}
                        onChange={(e) => setItemForm({ ...itemForm, timerName: e.target.value })}
                        placeholder="e.g., In Seats - Period 1"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Timer Type *</label>
                      <select
                        value={itemForm.timerType}
                        onChange={(e) => setItemForm({ ...itemForm, timerType: e.target.value })}
                        required
                      >
                        <option value="">Select type...</option>
                        <option value="seats">In Seats Warning</option>
                        <option value="bell">Bell Warning</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Time *</label>
                      <input
                        type="time"
                        value={itemForm.startTime}
                        onChange={(e) => setItemForm({ ...itemForm, startTime: e.target.value })}
                        required
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Days of Week</label>
                  <div className="days-selector">
                    {[
                      { label: 'Monday', value: 'monday' },
                      { label: 'Tuesday', value: 'tuesday' },
                      { label: 'Wednesday', value: 'wednesday' },
                      { label: 'Thursday', value: 'thursday' },
                      { label: 'Friday', value: 'friday' }
                    ].map(day => (
                      <label key={day.value} className="day-checkbox">
                        <input
                          type="checkbox"
                          checked={itemForm.daysOfWeek.includes(day.value)}
                          onChange={() => toggleDay(day.value)}
                        />
                        <span>{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="modal-buttons">
                  <button type="submit" className="save-button">
                    {editingItem ? 'Update' : 'Create'} Item
                  </button>
                  <button type="button" onClick={closeModal} className="cancel-button">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default DailySummaryManager;
